// ColumnSetEditor.jsx
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import "./columnSetEditor.scss";
import { ButtonControl, DialogControl, FormControl, IconControl, MultiSelectListControl, SidePanelFormControl } from "spark-core-dx/components";
import { getFieldDataTypeDisplayLabel } from "../utilities/fieldDataTypeDisplay";

/* ---------- Utils ---------- */

const EMPTY_SOURCE_OBJECT = {};
const EMPTY_ITEM_METADATA = { Fields: [] };
const VERSION_SUFFIX_RE = /\s*\(Version [^)]+\)$/i;
function stripVersionSuffix(name) {
  if (!name) return name;
  let prev, next = String(name);
  do { prev = next; next = next.replace(VERSION_SUFFIX_RE, ""); } while (next !== prev);
  return next;
}

function makeKey(sourceObject, storageKeyPrefix = "ColumnSet:") {
  if (!sourceObject) return null;
  const id =
    sourceObject.SourceObjectId ||
    sourceObject.EntityObjectId ||
    sourceObject._id ||
    sourceObject.UniqueId ||
    sourceObject.SourceObjectID ||
    sourceObject.EntityObjectID;
  return id ? `${storageKeyPrefix}${id}` : null;
}

function getSourceObjectVersion(sourceObject) {
  const version =
    sourceObject?.Version ??
    sourceObject?.version ??
    sourceObject?.DisplayVersion ??
    sourceObject?.displayVersion;
  if (version === undefined || version === null || String(version).trim() === "") return null;
  return String(version).trim();
}

function readFieldsFromSession(sourceObject, storageKeyPrefix) {
  const key = makeKey(sourceObject, storageKeyPrefix);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const sourceVersion = getSourceObjectVersion(sourceObject);
    const savedVersion = parsed?.SourceVersion === undefined || parsed?.SourceVersion === null
      ? null
      : String(parsed.SourceVersion).trim();
    if (sourceVersion && savedVersion !== sourceVersion) {
      sessionStorage.removeItem(key);
      return null;
    }
    return Array.isArray(parsed?.Fields) ? parsed.Fields : null;
  } catch {
    return null;
  }
}

function writeFieldsToSession(sourceObject, fields, storageKeyPrefix) {
  const key = makeKey(sourceObject, storageKeyPrefix);
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify({
      SourceVersion: getSourceObjectVersion(sourceObject),
      Fields: fields
    }));
  } catch {}
}

function comparableFields(fields) {
  return JSON.stringify((Array.isArray(fields) ? fields : []).map((field) => normalizeField(field)));
}

function comparableSourceObject(sourceObject) {
  const { Fields, fields, ...rest } = sourceObject ?? {};
  return JSON.stringify(rest ?? {});
}

function semanticSignature(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

function useSemanticallyStableValue(value) {
  const signature = semanticSignature(value);
  const stableRef = useRef({ signature, value });
  const changed = signature === null
    ? stableRef.current.value !== value
    : stableRef.current.signature !== signature;

  if (changed) {
    stableRef.current = { signature, value };
  }

  return stableRef.current.value;
}

function comparableDraft(sourceObject, fields) {
  return `${comparableSourceObject(sourceObject)}|${comparableFields(fields)}`;
}

function clearSessionForSourceObject(sourceObject, storageKeyPrefix) {
  const key = makeKey(sourceObject, storageKeyPrefix);
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {}
}

function safe(s) { return s ?? ""; }
function getFieldHeader(field) {
  return (
    field?.DisplayName ||
    field?.FieldName ||
    field?.SystemName ||
    field?.ObjectFieldName ||
    ""
  );
}
function getFieldNameHeader(field) {
  return (
    field?.FieldName ||
    field?.SystemName ||
    field?.ObjectFieldName ||
    field?.DisplayName ||
    ""
  );
}
function getFieldTypeBadge(field) {
  return getFieldDataTypeDisplayLabel(field, "");
}

const getOperationKey = (operation) =>
  operation?.Name || operation?.OperationTypeId || operation?.Value || operation?._id || operation?.Id || "";

const getOperationLabel = (operation) =>
  operation?.DisplayName || operation?.Text || getOperationKey(operation);

const getFieldId = (field) =>
  field?._id || field?.UniqueId || field?.SourceObjectFieldId || field?.EntityObjectFieldId || field?.Id || null;

const getReorderSequenceField = (mode) => (mode === "display" ? "DisplaySequence" : "GridSequence");

const getFieldSequence = (field, mode) => {
  if (mode === "display") {
    return Number(field?.DisplaySequence ?? field?.Sequence ?? field?.GridSequence ?? 0) || 0;
  }
  return Number(field?.GridSequence ?? field?.Sequence ?? 0) || 0;
};

const normalizeSelectValue = (value) => (value === null || value === undefined ? "" : String(value));

const toSelectedIdArray = (value) => {
  if (Array.isArray(value)) return value.map(normalizeSelectValue).filter(Boolean);
  const normalized = normalizeSelectValue(value);
  return normalized ? [normalized] : [];
};

// --- Boolean/number normalization helpers ---
const asBool = (v) =>
  v === true || v === 1 || v === "1" || (typeof v === "string" && v.toLowerCase() === "true");

function getFieldKeyBadge(field) {
  const isPrimary =
    asBool(field?.PrimaryField) ||
    asBool(field?.IsPrimary) ||
    asBool(field?.IsPrimaryKey) ||
    asBool(field?.PrimaryKey);
  const isUnique =
    asBool(field?.IsUniqueKey) ||
    asBool(field?.IsUnique) ||
    asBool(field?.Unique);

  if (isPrimary) return "PK/Unique";
  if (isUnique) return "Unique";
  return "";
}

const normalizeField = (f) => {
  if (!f) return f;
  const next = { ...f };
  if ("IsActive" in next)      next.IsActive = asBool(next.IsActive);
  if ("IsDisabled" in next)    next.IsDisabled = asBool(next.IsDisabled);
  if ("GridSequence" in next)  next.GridSequence = Number(next.GridSequence) || 0;
  if ("DisplaySequence" in next) next.DisplaySequence = Number(next.DisplaySequence) || 0;
  return next;
};

const isSubGridField = (field) => {
  const fieldType = String(field?.FieldType ?? field?.fieldType ?? "").trim().toLowerCase();
  return fieldType === "100" || fieldType === "subgrid" || fieldType === "grid";
};

const getNestedFieldCollection = (field) => {
  const candidates = [
    { fieldName: "EntityObjectFields", value: field?.EntityObjectFields ?? field?.entityObjectFields },
    { fieldName: "SourceObjectFields", value: field?.SourceObjectFields ?? field?.sourceObjectFields },
  ];
  const populated = candidates.find((candidate) => Array.isArray(candidate.value) && candidate.value.length > 0);
  if (populated) return { ...populated, value: populated.value };
  if (!isSubGridField(field)) return null;

  const declared = candidates.find((candidate) => Array.isArray(candidate.value));
  return declared ? { ...declared, value: declared.value } : null;
};

const getMetadataFieldName = (field) => field?.FieldName ?? field?.fieldName ?? field?.Name ?? field?.name ?? "";

const buildNestedTabFormFields = (formFields, collectionFieldName, tabTitle) =>
  (Array.isArray(formFields) ? formFields : []).map((field) => (
    String(getMetadataFieldName(field)).toLowerCase() === String(collectionFieldName).toLowerCase()
      ? {
          ...field,
          FieldName: collectionFieldName,
          DisplayName: `${tabTitle} Fields`,
          TabId: `${tabTitle}|5`,
        }
      : field
  ));

/* ---------- Reusable card ---------- */

function SectionCard({ title, subtitle, right, children }) {
  return (
    <div className="ps-card">
      {(title || right || subtitle) && (
        <div className="ps-card-header">
          <div>
            {title && <div className="ps-card-title">{title}</div>}
            {subtitle && <div className="ps-card-subtitle">{subtitle}</div>}
          </div>
          {right && <div className="ps-card-header-actions">{right}</div>}
        </div>
      )}
      <div className="ps-card-body">{children}</div>
    </div>
  );
}

function InlineFieldForm({ metadata, formFields, field, onChangeValue, onReady, subGridRenderers }) {
  const onReadyRef = useRef(onReady);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    onReadyRef.current?.();
  }, []);

  return (
    <FormControl
      metadata={metadata}
      formFields={formFields}
      itemData={field}
      disableFooter={true}
      disableMinWidth={true}
      groupId="columnSetEditorFieldInlineEdit"
      tabLayout="top"
      subGridRenderers={subGridRenderers}
      onChange={(value, fieldName) => onChangeValue(field, fieldName, value)}
      onSave={() => {}}
    />
  );
}

function NestedFieldCollectionEditor({
  parentField,
  collectionFieldName,
  childFields,
  fieldItemMetadata,
  onChange,
  storageKeyPrefix,
  fieldEditingPresentation = "inline",
  fillAvailableHeight = false,
}) {
  const stableParentField = useSemanticallyStableValue(parentField);
  const stableChildFields = useSemanticallyStableValue(Array.isArray(childFields) ? childFields : []);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const tabTitle = getFieldHeader(stableParentField) || "Nested";
  const nestedObject = useMemo(() => ({
    ...stableParentField,
    DisplayName: `${tabTitle} Fields`,
    Fields: stableChildFields,
  }), [stableChildFields, stableParentField, tabTitle]);
  const handleNestedChange = useCallback((updatedObject) => {
    onChangeRef.current?.(updatedObject?.Fields ?? []);
  }, []);
  const handleNestedSave = useCallback(async (updatedObject) => ({
    Success: true,
    Items: [updatedObject],
  }), []);

  return (
    <div
      className={`cs-nested-field-collection${fillAvailableHeight ? " cs-nested-field-collection--panel" : ""}`}
      data-testid={`nested-field-collection-${collectionFieldName}`}
    >
      <ColumnSetEditor
        sourceObject={nestedObject}
        fieldItemMetadata={fieldItemMetadata}
        sourceObjectItemMetadata={EMPTY_ITEM_METADATA}
        onSave={handleNestedSave}
        onChange={handleNestedChange}
        objectLabel={`${tabTitle} Field`}
        activeTitle={`Active ${tabTitle} Fields`}
        activeSubtitle={`Fields currently active in ${tabTitle}.`}
        availableTitle={`Available ${tabTitle} Fields`}
        availableSubtitle={`Fields available in ${tabTitle}.`}
        storageKeyPrefix={`${storageKeyPrefix}Nested:${collectionFieldName}:`}
        sidePanelEditingEnabled={false}
        showSourceObjectEditAction={false}
        showSourceObjectOperations={false}
        responsiveAvailablePickerEnabled={true}
        embeddedCollectionMode={true}
        fieldEditingPresentation={fieldEditingPresentation}
      />
    </div>
  );
}

/**
 * ColumnSetEditor
 *
 * Props:
 * - sourceObject
 * - refresh
 * - onSave(updatedSourceObject)
 * - onChange(updatedSourceObject)
 * - onEditField?(field)
 * - fieldItemMetadata
 * - sourceObjectItemMetadata
 * - sidePanelEditingEnabled
 * - editObjectRequestKey
 * - showSourceObjectEditAction
 * - showSourceObjectOperations
 * - titleVersion
 * - titleVersionPrefix
 */
export function ColumnSetEditor({
  sourceObject,
  refresh,
  onSave,
  onChange,
  fieldItemMetadata,
  sourceObjectItemMetadata,
  sourceObjectOperations = [],
  onSourceObjectOperationClick,
  objectLabel = "Source Object",
  objectDesignerName = "Column Set Editor",
  activeTitle = "Active Columns",
  activeSubtitle = "Columns currently shown on the grid.",
  availableTitle = "Available Columns",
  availableSubtitle = "Columns not currently shown on the grid.",
  objectActionsLabel = "Source Object Actions",
  noObjectActionsLabel = "No source actions available",
  editObjectTitle = "Edit Source Object",
  saveImpactMessage = "Any mappings may be affected if this source object configuration is saved.",
  storageKeyPrefix = "ColumnSet:",
  sidePanelEditingEnabled = true,
  editObjectRequestKey = 0,
  showSourceObjectEditAction = true,
  showSourceObjectOperations = true,
  responsiveAvailablePickerEnabled = false,
  embeddedCollectionMode = false,
  fieldEditingPresentation = "inline",
  titleVersion = "",
  titleVersionPrefix = "Version ",
}) {
  const so = useSemanticallyStableValue(sourceObject ?? EMPTY_SOURCE_OBJECT);
  const stableRefresh = useSemanticallyStableValue(refresh);
  const soId =
    so.SourceObjectId || so.EntityObjectId || so._id || so.UniqueId || so.SourceObjectID || so.EntityObjectID || null;

  // Initial fields: prefer sessionStorage, then inbound
  const initialFields = useMemo(() => {
    const saved = readFieldsFromSession(so, storageKeyPrefix);
    if (saved) return saved.map(normalizeField);
    return Array.isArray(so.Fields) ? so.Fields.map(normalizeField) : [];
  }, [so, storageKeyPrefix]);

  const [fields, setFields] = useState(initialFields);
  const [sourceDraft, setSourceDraft] = useState(so);
  const [undoFields, setUndoFields] = useState(null);
  const [canUndo, setCanUndo] = useState(false);

  // Side panel state for editing a single field
  const [openEditSidePanel, setOpenEditSidePanel] = useState(false);
  const [editItemData, setEditItemData] = useState(null);
  const [openSourceObjectSidePanel, setOpenSourceObjectSidePanel] = useState(false);
  const [sourceObjectEditData, setSourceObjectEditData] = useState(null);
  const [expandedFieldId, setExpandedFieldId] = useState(null);
  const [loadingFieldEditorId, setLoadingFieldEditorId] = useState(null);
  const [renderingFieldEditorId, setRenderingFieldEditorId] = useState(null);
  const [operationMenuOpen, setOperationMenuOpen] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [savePending, setSavePending] = useState(false);
  const operationMenuRef = useRef(null);
  const lastEditObjectRequestKeyRef = useRef(editObjectRequestKey);
  const onChangeRef = useRef(onChange);
  const lastEmittedDraftRef = useRef(comparableDraft(so, initialFields));

  const [searchActive, setSearchActive] = useState("");
  const [searchAvailable, setSearchAvailable] = useState("");
  const [responsiveAvailableColumnIds, setResponsiveAvailableColumnIds] = useState([]);
  const [leftSort, setLeftSort] = useState("grid"); // 'grid' | 'display' | 'name'
  const [rightSort, setRightSort] = useState("name"); // 'name' | 'type'
  const [reorderMode, setReorderMode] = useState("grid"); // 'grid' | 'display'
  const [draggingFieldId, setDraggingFieldId] = useState(null);
  const [dragOverFieldId, setDragOverFieldId] = useState(null);
  const hasSourceObjectOperations = showSourceObjectOperations && Array.isArray(sourceObjectOperations);

  /* ---------- Keep the latest parent callback without turning callback identity into a data change ---------- */
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  /* ---------- Reload when sourceObject or refresh changes ---------- */
  useEffect(() => {
    let inbound;
    let nextSourceDraft;
    let notificationBaselineFields;
    if (stableRefresh && typeof stableRefresh === "object" && Array.isArray(stableRefresh.Fields)) {
      inbound = stableRefresh.Fields.map(normalizeField);
      nextSourceDraft = { ...stableRefresh, Fields: inbound };
      notificationBaselineFields = inbound;
      writeFieldsToSession(so, inbound, storageKeyPrefix);
    } else {
      const saved = readFieldsFromSession(so, storageKeyPrefix);
      const sourceFields = Array.isArray(so.Fields) ? so.Fields.map(normalizeField) : [];
      inbound = saved
        ? saved.map(normalizeField)
        : sourceFields;
      nextSourceDraft = so;
      notificationBaselineFields = saved && comparableFields(saved) !== comparableFields(sourceFields)
        ? sourceFields
        : inbound;
      writeFieldsToSession(so, inbound, storageKeyPrefix);
    }

    lastEmittedDraftRef.current = comparableDraft(nextSourceDraft, notificationBaselineFields);
    setFields((previous) => comparableFields(previous) === comparableFields(inbound) ? previous : inbound);
    setSourceDraft((previous) => (
      comparableSourceObject(previous) === comparableSourceObject(nextSourceDraft)
        ? previous
        : nextSourceDraft
    ));
    setUndoFields(null);
    setCanUndo(false);
  }, [soId, so, stableRefresh, storageKeyPrefix]);

  /* ---------- Notify only after a semantic draft mutation, never during hydration ---------- */
  useEffect(() => {
    const updatedSourceObject = { ...(sourceDraft ?? so ?? {}), Fields: fields };
    const nextSignature = comparableDraft(updatedSourceObject, fields);
    if (nextSignature === lastEmittedDraftRef.current) return;

    lastEmittedDraftRef.current = nextSignature;
    if (typeof onChangeRef.current === "function") {
      try {
        onChangeRef.current(updatedSourceObject);
      } catch {}
    }
  }, [fields, sourceDraft, so]);

  useEffect(() => {
    if (!operationMenuOpen) return undefined;

    const handleDocumentMouseDown = (event) => {
      if (!operationMenuRef.current || operationMenuRef.current.contains(event.target)) return;
      setOperationMenuOpen(false);
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
  }, [operationMenuOpen]);

  useEffect(() => {
    if (!loadingFieldEditorId) return undefined;
    const scheduleFrame = typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame.bind(window)
      : (callback) => window.setTimeout(callback, 0);
    const cancelFrame = typeof window.cancelAnimationFrame === "function"
      ? window.cancelAnimationFrame.bind(window)
      : window.clearTimeout.bind(window);

    let secondFrameId = null;
    const firstFrameId = scheduleFrame(() => {
      secondFrameId = scheduleFrame(() => {
        setRenderingFieldEditorId(loadingFieldEditorId);
      });
    });

    return () => {
      cancelFrame(firstFrameId);
      if (secondFrameId !== null) cancelFrame(secondFrameId);
    };
  }, [loadingFieldEditorId]);

  /* ---------- Derived lists (ONLY IsActive controls visibility) ---------- */
  const isActive = (f) => asBool(f?.IsActive);

  const activeColumns = useMemo(
    () => fields.filter(isActive),
    [fields]
  );
  const inactiveColumns = useMemo(
    () => fields.filter((f) => !isActive(f)),
    [fields]
  );

  const hasUnsavedChanges = useMemo(() => {
    const savedFields = Array.isArray(so.Fields) ? so.Fields.map(normalizeField) : [];
    return (
      comparableFields(fields) !== comparableFields(savedFields) ||
      comparableSourceObject(sourceDraft) !== comparableSourceObject(so)
    );
  }, [fields, so, sourceDraft]);

  const sourceObjectFormFields = useMemo(
    () =>
      (Array.isArray(sourceObjectItemMetadata?.Fields) ? sourceObjectItemMetadata.Fields : []).filter((field) => {
        const fieldName = String(field?.FieldName ?? field?.Name ?? "").toLowerCase();
        return fieldName !== "fields";
      }),
    [sourceObjectItemMetadata]
  );
  const fieldFormFields = useMemo(
    () => (Array.isArray(fieldItemMetadata?.Fields) ? fieldItemMetadata.Fields : []),
    [fieldItemMetadata]
  );
  const inlineFieldFormMetadata = useMemo(
    () => (fieldItemMetadata ? { ...fieldItemMetadata, Name: "" } : null),
    [fieldItemMetadata]
  );

  const sortedActive = useMemo(() => {
    const arr = [...activeColumns];
    arr.sort((a, b) => {
      if (leftSort === "grid" || leftSort === "display") {
        const ga = getFieldSequence(a, leftSort);
        const gb = getFieldSequence(b, leftSort);
        if (ga !== gb) return ga - gb;
      }
      const ha = getFieldHeader(a);
      const hb = getFieldHeader(b);
      return ha.localeCompare(hb);
    });
    const q = searchActive.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((f) => {
      const hay = (
        getFieldHeader(f) +
        " " +
        safe(f.Description) +
        " " +
        safe(f.SystemName)
      ).toLowerCase();
      return hay.includes(q);
    });
  }, [activeColumns, leftSort, searchActive]);

  const sortedInactive = useMemo(() => {
    const arr = [...inactiveColumns];
    arr.sort((a, b) => {
      if (rightSort === "type") {
        const ta = getFieldTypeBadge(a);
        const tb = getFieldTypeBadge(b);
        if (ta !== tb) return ta.localeCompare(tb);
      }
      const ha = getFieldHeader(a);
      const hb = getFieldHeader(b);
      return ha.localeCompare(hb);
    });
    const q = searchAvailable.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((f) => {
      const hay = (
        getFieldHeader(f) +
        " " +
        safe(f.Description) +
        " " +
        safe(f.SystemName)
      ).toLowerCase();
      return hay.includes(q);
    });
  }, [inactiveColumns, rightSort, searchAvailable]);

  const availableColumnSelectData = useMemo(() => {
    const arr = [...inactiveColumns];
    arr.sort((a, b) => getFieldHeader(a).localeCompare(getFieldHeader(b)));

    return arr.map((field, index) => {
      const fieldId = getFieldId(field);
      const header = getFieldHeader(field);
      const typeBadge = getFieldTypeBadge(field);

      return {
        Text: typeBadge ? `${header} (${typeBadge})` : header,
        Value: fieldId ? String(fieldId) : "",
        Disabled: !fieldId,
        SortOrder: index + 1,
      };
    });
  }, [inactiveColumns]);

  useEffect(() => {
    if (!responsiveAvailablePickerEnabled) return;

    const availableIds = new Set(
      inactiveColumns
        .map((field) => getFieldId(field))
        .filter(Boolean)
        .map(String)
    );

    setResponsiveAvailableColumnIds((previous) => {
      const previousIds = toSelectedIdArray(previous);
      const nextIds = previousIds.filter((fieldId) => availableIds.has(fieldId));
      const unchanged = nextIds.length === previousIds.length && nextIds.every((fieldId, index) => fieldId === previousIds[index]);
      return unchanged ? previous : nextIds;
    });
  }, [inactiveColumns, responsiveAvailablePickerEnabled]);

  /* ---------- Undo helpers ---------- */
  const clearUndo = () => { setUndoFields(null); setCanUndo(false); };
  const invalidateUndoOnEdit = () => { clearUndo(); };

  /* ---------- Field mutators (use _id; normalize) ---------- */
  function updateFields(updateFn) {
    setFields((prev) => {
      const next = typeof updateFn === "function" ? updateFn(prev) : updateFn;
      if (next === prev || comparableFields(next) === comparableFields(prev)) return prev;
      writeFieldsToSession(so, next, storageKeyPrefix);
      return next;
    });
  }
  function updateField(fieldId, patch) {
    if (!fieldId) return;
    invalidateUndoOnEdit();
    updateFields((prev) =>
      prev.map((f) => (getFieldId(f) === fieldId ? normalizeField({ ...f, ...patch }) : f))
    );
  }
  function activateColumn(field) {
    if (!field) return;
    const id = getFieldId(field);
    invalidateUndoOnEdit();
    const maxGridSeq = fields.reduce((max, f) => {
      const gs = typeof f?.GridSequence === "number" ? f.GridSequence : 0;
      return gs > max ? gs : max;
    }, 0);
    updateField(id, {
      IsActive: true,
      IsDisabled: false,
      GridSequence: maxGridSeq + 1,
    });
  }
  function activateSelectedAvailableColumns() {
    const selectedIds = toSelectedIdArray(responsiveAvailableColumnIds);
    if (selectedIds.length === 0) return;

    const selectedIdSet = new Set(selectedIds);
    invalidateUndoOnEdit();

    updateFields((prev) => {
      let nextGridSequence = prev.reduce((max, f) => {
        const gs = Number(f?.GridSequence) || 0;
        return gs > max ? gs : max;
      }, 0);

      let activatedCount = 0;
      const next = prev.map((field) => {
        const fieldId = getFieldId(field);
        if (!fieldId || !selectedIdSet.has(String(fieldId)) || isActive(field)) return field;

        nextGridSequence += 1;
        activatedCount += 1;
        return normalizeField({
          ...field,
          IsActive: true,
          IsDisabled: false,
          GridSequence: nextGridSequence,
        });
      });

      return activatedCount > 0 ? next : prev;
    });

    setResponsiveAvailableColumnIds([]);
  }
  function deactivateColumn(field) {
    if (!field) return;
    const id = getFieldId(field);
    invalidateUndoOnEdit();
    setExpandedFieldId((current) => String(current || "") === String(id) ? null : current);
    setLoadingFieldEditorId((current) => String(current || "") === String(id) ? null : current);
    setRenderingFieldEditorId((current) => String(current || "") === String(id) ? null : current);
    updateField(id, { IsActive: false });
  }

  function reorderActiveColumn(dragFieldId, targetFieldId) {
    if (!dragFieldId || !targetFieldId || String(dragFieldId) === String(targetFieldId)) return;
    invalidateUndoOnEdit();

    const sequenceField = getReorderSequenceField(reorderMode);

    updateFields((prev) => {
      const activeOrdered = prev
        .filter(isActive)
        .slice()
        .sort((a, b) => {
          const aSequence = getFieldSequence(a, reorderMode);
          const bSequence = getFieldSequence(b, reorderMode);
          if (aSequence !== bSequence) return aSequence - bSequence;
          return getFieldHeader(a).localeCompare(getFieldHeader(b));
        });

      const fromIndex = activeOrdered.findIndex((field) => String(getFieldId(field) || "") === String(dragFieldId));
      const toIndex = activeOrdered.findIndex((field) => String(getFieldId(field) || "") === String(targetFieldId));
      if (fromIndex < 0 || toIndex < 0) return prev;

      const reordered = [...activeOrdered];
      const [movedField] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, movedField);

      const nextSequences = new Map(
        reordered.map((field, index) => [String(getFieldId(field) || ""), index + 1])
      );

      return prev.map((field) => {
        const fieldId = String(getFieldId(field) || "");
        if (!nextSequences.has(fieldId)) return field;
        return normalizeField({
          ...field,
          [sequenceField]: nextSequences.get(fieldId),
        });
      });
    });
  }

  function handleChangeFieldValue(field, key, value) {
    const id = getFieldId(field);
    updateField(id, { [key]: value });
  }

  /* ---------- Save / Rollback / Clear ---------- */
  const handleSave = async () => {
    if (!hasUnsavedChanges) return { Success: true };
    setShowSaveConfirm(true);
    return { Success: true };
  };

  const handleConfirmSave = async () => {
    const updated = { ...(sourceDraft ?? so ?? {}), Fields: fields };
    setSavePending(true);
    if (onSave) {
      try {
        const result = await onSave(updated);
        if (result?.Success) {
          setUndoFields(null);
          setCanUndo(false);
          setShowSaveConfirm(false);
        }
        return result;
      } finally {
        setSavePending(false);
      }
    }

    setSavePending(false);
    setShowSaveConfirm(false);
    return { Success: true };
  };

  const handleRollbackOrUndo = () => {
    if (canUndo && undoFields) {
      setFields(undoFields);
      writeFieldsToSession(so, undoFields, storageKeyPrefix);
      clearUndo();
    } else {
      setUndoFields(fields);
      setCanUndo(true);
      clearSessionForSourceObject(so, storageKeyPrefix);
      const inbound = Array.isArray(so.Fields) ? so.Fields.map(normalizeField) : [];
      setFields(inbound);
      writeFieldsToSession(so, inbound, storageKeyPrefix);
      setSourceDraft(so);
    }
  };
  const handleHardClear = () => {
    clearSessionForSourceObject(so, storageKeyPrefix);
    const cleared = fields.map((f) => normalizeField({ ...f, IsActive: false }));
    setFields(cleared);
    setUndoFields(null);
    setCanUndo(false);
  };

  const handleEditSourceObjectClick = () => {
    if (!sidePanelEditingEnabled) return;
    setSourceObjectEditData({ ...(sourceDraft ?? so ?? {}), Fields: fields });
    setOpenSourceObjectSidePanel(true);
  };

  useEffect(() => {
    if (editObjectRequestKey === lastEditObjectRequestKeyRef.current) return;
    lastEditObjectRequestKeyRef.current = editObjectRequestKey;
    if (!editObjectRequestKey || !sidePanelEditingEnabled) return;
    setSourceObjectEditData({ ...(sourceDraft ?? so ?? {}), Fields: fields });
    setOpenSourceObjectSidePanel(true);
  }, [editObjectRequestKey, fields, sidePanelEditingEnabled, so, sourceDraft]);

  /* ---------- SidePanelFormControl handlers (field edit) ---------- */

  const handleFieldPanelCancel = () => {
    setOpenEditSidePanel(false);
    setEditItemData(null);
  };

  const handleOpenFieldPanel = (field) => {
    if (fieldEditingPresentation !== "sidePanel" || !field) return;
    setEditItemData(normalizeField({ ...field }));
    setOpenEditSidePanel(true);
  };

  const handleFieldPanelSave = async (updatedField) => {
    const id = getFieldId(updatedField) ?? getFieldId(editItemData);
    if (!id) {
      handleFieldPanelCancel();
      return { Success: true, Items: [updatedField] };
    }

    const normalized = normalizeField(updatedField);

    updateFields((prev) =>
      prev.map((f) => (getFieldId(f) === id ? { ...f, ...normalized } : f))
    );

    setOpenEditSidePanel(false);
    setEditItemData(null);

    return { Success: true, Items: [normalized] };
  };

  const handleSourceObjectPanelCancel = () => {
    setOpenSourceObjectSidePanel(false);
    setSourceObjectEditData(null);
  };

  const handleSourceObjectPanelSave = async (updatedSourceObject) => {
    setSourceDraft((previous) => ({
      ...(previous || so || {}),
      ...(updatedSourceObject || {}),
      Fields: fields,
    }));
    setOpenSourceObjectSidePanel(false);
    setSourceObjectEditData(null);
    return { Success: true, Items: [updatedSourceObject] };
  };

  const handleOperationClick = (operation) => {
    setOperationMenuOpen(false);
    if (typeof onSourceObjectOperationClick === "function") {
      onSourceObjectOperationClick(operation);
    }
  };

  /* ---------- Row components ---------- */

  const ActiveColumnRow = useMemo(() => {
  return function ActiveColumnRow({
    field,
    formFields,
    formMetadata,
    onDeactivate,
    onChangeValue,
    expanded = false,
    renderForm = false,
    loading = false,
    draggable = false,
    dragging = false,
    dragTarget = false,
    onToggleExpanded,
    onEditorReady,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    nestedStorageKeyPrefix,
    fieldEditingPresentation: rowFieldEditingPresentation,
    onOpenFieldPanel,
  }) {
    const header = getFieldHeader(field);
    const fieldName = getFieldNameHeader(field);
    const showFieldName = fieldName && fieldName !== header;
    const typeBadge = getFieldTypeBadge(field);
    const keyBadge = getFieldKeyBadge(field);
    const usesInlineFieldEditor = rowFieldEditingPresentation !== "sidePanel";
    const shouldRenderEditor = usesInlineFieldEditor && expanded;
    const toggleExpanded = () => onToggleExpanded(field);
    const nestedCollection = useMemo(() => getNestedFieldCollection(field), [field]);
    const nestedTabTitle = getFieldHeader(field) || "Nested";
    const effectiveFormFields = useMemo(() => nestedCollection
      ? buildNestedTabFormFields(formFields, nestedCollection.fieldName, nestedTabTitle)
      : formFields, [formFields, nestedCollection, nestedTabTitle]);
    const subGridRenderers = useMemo(() => nestedCollection
      ? {
          [nestedCollection.fieldName]: ({ updatedItem, value, onChange }) => (
            <NestedFieldCollectionEditor
              parentField={updatedItem || field}
              collectionFieldName={nestedCollection.fieldName}
              childFields={Array.isArray(value) ? value : nestedCollection.value}
              fieldItemMetadata={formMetadata}
              storageKeyPrefix={nestedStorageKeyPrefix}
              onChange={onChange}
              fieldEditingPresentation="inline"
            />
          ),
        }
      : undefined, [field, formMetadata, nestedCollection, nestedStorageKeyPrefix]);

    return (
      <div
        className={`ps-card ps-entry-card cs-column-card${expanded ? " cs-column-card--expanded" : ""}${draggable ? " cs-column-card--draggable" : ""}${dragging ? " cs-column-card--dragging" : ""}${dragTarget ? " cs-column-card--drag-target" : ""}`}
        draggable={draggable}
        onDragStart={(event) => onDragStart?.(field, event)}
        onDragOver={(event) => onDragOver?.(field, event)}
        onDrop={(event) => onDrop?.(field, event)}
        onDragEnd={(event) => onDragEnd?.(field, event)}
      >
        <div
          className="ps-entry-header"
          role={usesInlineFieldEditor ? "button" : undefined}
          tabIndex={usesInlineFieldEditor ? 0 : undefined}
          aria-expanded={usesInlineFieldEditor ? expanded : undefined}
          onClick={usesInlineFieldEditor ? toggleExpanded : undefined}
          onKeyDown={(event) => {
            if (!usesInlineFieldEditor) return;
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            toggleExpanded();
          }}
        >
          <div className="ps-entry-left">
            <div className="ps-entry-top">
              <span
                className={`cs-drag-handle material-symbols-rounded${draggable ? "" : " cs-drag-handle--disabled"}`}
                title={draggable ? "Drag to reorder" : "Switch to Display or Grid sequence sort to reorder"}
                aria-hidden="true"
              >
                drag_indicator
              </span>
              <button
                type="button"
                className="cs-edit-column-btn"
                title={usesInlineFieldEditor ? (expanded ? "Hide column details" : "Edit column details") : `Edit ${header || "field"}`}
                aria-label={usesInlineFieldEditor ? `${expanded ? "Hide" : "Edit"} ${header || "column"} details` : `Edit ${header || "field"}`}
                aria-expanded={usesInlineFieldEditor ? expanded : undefined}
                onClick={(event) => {
                  event.stopPropagation();
                  if (usesInlineFieldEditor) {
                    toggleExpanded();
                  } else {
                    onOpenFieldPanel(field);
                  }
                }}
              >
                {usesInlineFieldEditor ? (
                  <span className="material-symbols-rounded" aria-hidden="true">
                    {expanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                  </span>
                ) : <IconControl iconKey="edit" />}
              </button>
              <div className="ps-name" title={header}>{header}</div>
              {showFieldName && <span className="cs-field-name">({fieldName})</span>}
              {typeBadge && <span className="ps-badge">{typeBadge}</span>}
              {keyBadge && <span className="cs-key-badge">{keyBadge}</span>}
            </div>
          </div>
          <div className="ps-entry-right">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onDeactivate(field);
              }}
              className="cs-remove-column-btn"
              title="Remove from active columns"
              aria-label={`Remove ${header || "column"} from active columns`}
            >
              <span className="material-symbols-rounded" aria-hidden="true">remove_circle_outline</span>
            </button>
          </div>
        </div>

        {shouldRenderEditor ? (
          <div
            className={`cs-edit-row${expanded ? "" : " cs-edit-row--collapsed"}`}
            aria-hidden={!expanded}
          >
            <div className="cs-edit-form">
              {loading ? (
                <div className="cs-edit-loading" role="status" aria-live="polite">
                  Loading field editor...
                </div>
              ) : null}
              {renderForm && formMetadata && formFields?.length ? (
                <InlineFieldForm
                  metadata={formMetadata}
                  formFields={effectiveFormFields}
                  field={field}
                  onChangeValue={onChangeValue}
                  onReady={onEditorReady}
                  subGridRenderers={subGridRenderers}
                />
              ) : !loading ? (
                <div className="cs-edit-missing-metadata">Field metadata is not available.</div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  };
}, []); 

const InactiveColumnRow = useMemo(() => {
  return function InactiveColumnRow({ field, onActivate }) {
    const header = getFieldHeader(field);
    const typeBadge = getFieldTypeBadge(field);
    return (
      <li className="ps-item">
        <div className="ps-item-left">
          <div className="ps-item-top">
            <div className="ps-dname" title={header}>{header}</div>
            {typeBadge && <span className="ps-badge">{typeBadge}</span>}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onActivate(field)}
          className="ps-item-add"
          title="Add to active columns"
          aria-label={`Add ${header || "column"} to active columns`}
        >
          <span className="material-symbols-rounded" aria-hidden="true">add</span>
        </button>
      </li>
    );
  };
}, []); 

  /* ---------- Render ---------- */

  const baseTitle =
    stripVersionSuffix(
      sourceDraft?.DisplayName || sourceDraft?.SystemName || sourceDraft?.Name || so.DisplayName || so.SystemName || so.Name || objectDesignerName
    );
  const resolvedTitleVersion = String(
    titleVersion || sourceDraft?.DisplayVersion || so.DisplayVersion || ""
  ).trim();
  const title = resolvedTitleVersion
    ? `${baseTitle} (${titleVersionPrefix ?? ""}${resolvedTitleVersion})`
    : baseTitle;

  const desc =
    sourceDraft?.Description ||
    so.Description ||
    "Manage which fields/columns are active on the grid and edit their metadata.";
  const editItemNestedCollection = useMemo(() => getNestedFieldCollection(editItemData), [editItemData]);
  const editItemFormFields = useMemo(() => editItemNestedCollection
    ? buildNestedTabFormFields(fieldFormFields, editItemNestedCollection.fieldName, getFieldHeader(editItemData) || "Nested")
    : fieldFormFields, [editItemData, editItemNestedCollection, fieldFormFields]);
  const editItemSubGridRenderers = useMemo(() => editItemNestedCollection
    ? {
        [editItemNestedCollection.fieldName]: ({ updatedItem, value, onChange }) => (
          <NestedFieldCollectionEditor
            parentField={updatedItem || editItemData}
            collectionFieldName={editItemNestedCollection.fieldName}
            childFields={Array.isArray(value) ? value : editItemNestedCollection.value}
            fieldItemMetadata={inlineFieldFormMetadata}
            storageKeyPrefix={storageKeyPrefix}
            onChange={onChange}
            fieldEditingPresentation="inline"
            fillAvailableHeight={true}
          />
        ),
      }
    : undefined, [editItemData, editItemNestedCollection, inlineFieldFormMetadata, storageKeyPrefix]);

  return (
    <div
      className={`column-set-editor${responsiveAvailablePickerEnabled ? " cs-responsive-available-enabled" : ""}${embeddedCollectionMode ? " cs-embedded-field-collection" : ""}`}
      data-object-label={objectLabel}
    >
      <div className="ps-root">
        {/* Header */}
        {!embeddedCollectionMode ? <div className="ps-header" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="ps-header-left">
            <div className="ps-title" title={title}>{title}</div>
            {desc && <div className="ps-desc">{desc}</div>}
          </div>

          <div className="ps-actions" style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <ButtonControl type="standard" onClick={handleRollbackOrUndo}
              title={canUndo ? "Undo last Rollback (restore the previous working copy)"
                             : "Rollback to the server state (you can Undo this)"}>
              {canUndo ? "Undo Rollback" : "Rollback"}
            </ButtonControl>
            <ButtonControl type="ok" onClick={handleSave} disabled={!hasUnsavedChanges} title={`Save ${objectLabel.toLowerCase()} configuration`}>
              Save
            </ButtonControl>
            <ButtonControl type="cancel" onClick={handleHardClear}
              title="Clear active columns: mark all fields inactive and wipe session buffer">
              Clear
            </ButtonControl>
            <div className="cs-toolbar-spacer" />
            {showSourceObjectEditAction && sidePanelEditingEnabled ? (
              <button
                type="button"
                className="cs-toolbar-icon-btn"
                title={editObjectTitle}
                onClick={handleEditSourceObjectClick}
              >
                <IconControl iconKey={"edit"} />
              </button>
            ) : null}
            {hasSourceObjectOperations && (
              <div className="cs-operation-menu" ref={operationMenuRef}>
                <button
                  type="button"
                  className="cs-toolbar-icon-btn"
                  title={objectActionsLabel}
                  aria-label={objectActionsLabel}
                  aria-haspopup="menu"
                  aria-expanded={operationMenuOpen}
                  onClick={() => setOperationMenuOpen((value) => !value)}
                >
                  <IconControl iconKey={"viewmore"} />
                </button>
                {operationMenuOpen && (
                  <div className="cs-operation-list" role="menu">
                    {sourceObjectOperations.length > 0 ? (
                      sourceObjectOperations.map((operation) => (
                        <button
                          type="button"
                          role="menuitem"
                          key={getOperationKey(operation)}
                          className="cs-operation-item"
                          onClick={() => handleOperationClick(operation)}
                        >
                          {getOperationLabel(operation)}
                        </button>
                      ))
                    ) : (
                      <div className="cs-operation-empty">{noObjectActionsLabel}</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div> : null}

        {/* Two-pane layout */}
        {responsiveAvailablePickerEnabled ? (
          <div className="cs-responsive-available-picker" data-testid="responsive-available-columns-picker">
            <div className="cs-responsive-picker-label">{availableTitle}</div>
            <div className="cs-responsive-picker-controls">
              <div className="cs-responsive-picker-control">
                <MultiSelectListControl
                  metadata={{
                    FieldName: "ResponsiveAvailableColumns",
                    DataJson: availableColumnSelectData,
                    PlaceHolder: "Select available columns...",
                  }}
                  value={responsiveAvailableColumnIds}
                  onChange={(value) => setResponsiveAvailableColumnIds(toSelectedIdArray(value))}
                  disabled={availableColumnSelectData.length === 0}
                  enableTitleTag={true}
                />
              </div>
              <ButtonControl
                type="ok"
                onClick={activateSelectedAvailableColumns}
                disabled={responsiveAvailableColumnIds.length === 0}
                title="Add selected available columns"
              >
                Add
              </ButtonControl>
            </div>
          </div>
        ) : null}
        <div className="ps-grid">
          {/* Left: Active Columns */}
          <div className="ps-card-left">
            <SectionCard
              title={`${activeTitle} (${activeColumns.length})`}
              subtitle={activeSubtitle}
              right={
                <div className="ps-controls">
                  <div className="cs-reorder-mode" role="group" aria-label="Reorder mode">
                    <button
                      type="button"
                      className={`cs-reorder-mode__button${reorderMode === "display" ? " active" : ""}`}
                      onClick={() => {
                        setReorderMode("display");
                        setLeftSort("display");
                      }}
                    >
                      Display
                    </button>
                    <button
                      type="button"
                      className={`cs-reorder-mode__button${reorderMode === "grid" ? " active" : ""}`}
                      onClick={() => {
                        setReorderMode("grid");
                        setLeftSort("grid");
                      }}
                    >
                      Grid
                    </button>
                  </div>
                  <select
                    value={leftSort}
                    onChange={(e) => {
                      const nextSort = e.target.value === "name" ? "name" : e.target.value === "display" ? "display" : "grid";
                      setLeftSort(nextSort);
                      if (nextSort === "display" || nextSort === "grid") {
                        setReorderMode(nextSort);
                      }
                    }}
                    className="ps-select"
                  >
                    <option value="grid">Sort: Grid</option>
                    <option value="display">Sort: Display</option>
                    <option value="name">Sort: Name</option>
                  </select>
                  <input
                    type="text"
                    value={searchActive}
                    onChange={(e) => setSearchActive(e.target.value)}
                    placeholder="Search active columns..."
                    className="ps-input"
                  />
                </div>
              }
            >
              {sortedActive.length === 0 ? (
                <div className="ps-empty">No active columns. Add columns from the right.</div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {sortedActive.map((f) => (
                    (() => {
                      const fieldId = getFieldId(f);
                      const fieldIdText = String(fieldId || "");
                      const editorRendering = String(renderingFieldEditorId || "") === fieldIdText;
                      const canDragReorder = (leftSort === "grid" || leftSort === "display") && searchActive.trim().length === 0;
                      return (
                        <ActiveColumnRow
                          key={fieldId}
                          field={f}
                          formFields={fieldFormFields}
                          formMetadata={inlineFieldFormMetadata}
                          expanded={String(expandedFieldId || "") === String(fieldId)}
                          renderForm={editorRendering}
                          loading={String(loadingFieldEditorId || "") === String(fieldId)}
                          draggable={canDragReorder}
                          dragging={String(draggingFieldId || "") === fieldIdText}
                          dragTarget={String(dragOverFieldId || "") === fieldIdText && String(draggingFieldId || "") !== fieldIdText}
                          onToggleExpanded={(field) => {
                            const nextFieldId = getFieldId(field);
                            const willExpand = String(expandedFieldId || "") !== String(nextFieldId);
                            setRenderingFieldEditorId(null);
                            setLoadingFieldEditorId(willExpand ? nextFieldId : null);
                            setExpandedFieldId((current) => String(current || "") === String(nextFieldId) ? null : nextFieldId);
                          }}
                          onEditorReady={() => {
                            setLoadingFieldEditorId((current) => String(current || "") === fieldIdText ? null : current);
                          }}
                          onDeactivate={deactivateColumn}
                          onChangeValue={handleChangeFieldValue}
                          nestedStorageKeyPrefix={storageKeyPrefix}
                          fieldEditingPresentation={fieldEditingPresentation}
                          onOpenFieldPanel={handleOpenFieldPanel}
                          onDragStart={(field, event) => {
                            if (!canDragReorder) return;
                            const nextFieldId = getFieldId(field);
                            setDraggingFieldId(nextFieldId);
                            setDragOverFieldId(null);
                            event.dataTransfer.effectAllowed = "move";
                            event.dataTransfer.setData("text/plain", String(nextFieldId || ""));
                          }}
                          onDragOver={(field, event) => {
                            if (!canDragReorder || !draggingFieldId) return;
                            event.preventDefault();
                            event.dataTransfer.dropEffect = "move";
                            setDragOverFieldId(getFieldId(field));
                          }}
                          onDrop={(field, event) => {
                            if (!canDragReorder) return;
                            event.preventDefault();
                            const droppedFieldId = getFieldId(field);
                            reorderActiveColumn(draggingFieldId, droppedFieldId);
                            setDraggingFieldId(null);
                            setDragOverFieldId(null);
                          }}
                          onDragEnd={() => {
                            setDraggingFieldId(null);
                            setDragOverFieldId(null);
                          }}
                        />
                      );
                    })()
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          {/* Right: Inactive Columns */}
          <div className="ps-card-right">
            <SectionCard
              title={`${availableTitle} (${inactiveColumns.length})`}
              subtitle={availableSubtitle}
              right={
                <div className="ps-controls">
                  <select
                    value={rightSort}
                    onChange={(e) => setRightSort(e.target.value === "type" ? "type" : "name")}
                    className="ps-select"
                  >
                    <option value="name">Sort: Name</option>
                    <option value="type">Sort: Type</option>
                  </select>
                  <input
                    type="text"
                    value={searchAvailable}
                    onChange={(e) => setSearchAvailable(e.target.value)}
                    placeholder="Search available..."
                    className="ps-input"
                  />
                </div>
              }
            >
              {sortedInactive.length === 0 ? (
                <div className="ps-empty">All fields are active or none match the search.</div>
              ) : (
                <div className="ps-available-scroll">
                  <ul className="ps-list">
                    {sortedInactive.map((f) => (
                      <InactiveColumnRow
                        key={`${getFieldId(f)}-inactive`}
                        field={f}
                        onActivate={activateColumn}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      </div>

      {sidePanelEditingEnabled && fieldEditingPresentation === "sidePanel" ? (
        <SidePanelFormControl
          key={
            (editItemData?._id ?? editItemData?.UniqueId) ||
            (openEditSidePanel ? "open" : "closed")
          }
          open={openEditSidePanel}
          title={editItemData?.DisplayName ? `Edit: ${editItemData.DisplayName}` : "Edit Column"}

          /* what SidePanelFormControl expects */
          metadata={fieldItemMetadata}
          formFields={editItemFormFields}
          itemData={editItemData}
          subGridRenderers={editItemSubGridRenderers}

          /* lifecycle */
          onSave={handleFieldPanelSave}
          onCancel={handleFieldPanelCancel}
          onAnimationEnd={(e) => {
            if (e.target !== e.currentTarget) return;
            if (editItemData && !openEditSidePanel && e.animationName === "sidePanelExit") {
              setEditItemData(null);
            }
          }}

          /* SidePanel will call this during width changes */
          onUpdateSidePanel={() => {}}

          /* state setters used internally by SidePanelFormControl */
          setOpenEditSidePanel={setOpenEditSidePanel}
          setItemData={setEditItemData}

          viewOnly={false}
        />
      ) : null}

      {sidePanelEditingEnabled && sourceObjectItemMetadata ? (
        <SidePanelFormControl
          key={
            (sourceObjectEditData?._id ?? sourceObjectEditData?.UniqueId ?? soId ?? "source-object-edit") +
            (openSourceObjectSidePanel ? "-open" : "-closed")
          }
          open={openSourceObjectSidePanel}
          title={sourceDraft?.DisplayName ? `Edit: ${sourceDraft.DisplayName}` : editObjectTitle}
          metadata={sourceObjectItemMetadata}
          formFields={sourceObjectFormFields}
          itemData={sourceObjectEditData}
          onSave={handleSourceObjectPanelSave}
          onCancel={handleSourceObjectPanelCancel}
          onAnimationEnd={(e) => {
            if (e.target !== e.currentTarget) return;
            if (sourceObjectEditData && !openSourceObjectSidePanel && e.animationName === "sidePanelExit") {
              setSourceObjectEditData(null);
            }
          }}
          onUpdateSidePanel={() => {}}
          setOpenEditSidePanel={(value) => {
            setOpenSourceObjectSidePanel(value);
            if (!value) setSourceObjectEditData(null);
          }}
          setItemData={setSourceObjectEditData}
          viewOnly={false}
        />
      ) : null}

      <DialogControl
        openDialog={showSaveConfirm}
        title={"Confirm Save"}
        onX={() => setShowSaveConfirm(false)}
        onCancel={() => setShowSaveConfirm(false)}
        onOk={handleConfirmSave}
        cancelText={"Cancel"}
        okText={savePending ? "Saving..." : "Save Changes"}
        disableOk={savePending}
      >
        <div className="cs-confirm-dialog">
          {saveImpactMessage}
        </div>
      </DialogControl>
    </div>
  );
}

export default ColumnSetEditor;
